/**
 * ObjectiveCreatePage — `/objectives/new`
 *
 * Paso 0: el usuario elige el tipo de campaña.
 * Muestra el picker de los 18 presets + un bloque grande "Agregar campaña
 * personalizada" que linkea a /objectives/new/custom (página dedicada).
 *
 * Al confirmar con un preset normal: crea draft y navega al setup wizard.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  AlertCircle,
  Sparkles,
  Wand2,
  Zap,
  Info,
} from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { useObjectives } from "../hooks/useObjectives";
import { objectivesApi } from "../api";
import { ObjectivePresetPicker } from "../components/ObjectivePresetPicker";
import { PRESET_BY_KEY } from "../types";
import type { ObjectivePreset } from "../types";

const MOCK_BRAND_ID = "brand-demo";

/**
 * Presets que típicamente corren como Automáticas always-on. Si el usuario
 * elige uno de estos, le mostramos un banner explicando que ya hay una
 * always-on activa y le ofrecemos verla en vez de duplicar.
 */
const TYPICALLY_AUTOMATIC_PRESETS = new Set<ObjectivePreset>([
  "cart_abandon",
  "winback_general",
  "welcome_first",
  "birthday",
  "cross_sell",
  "reorder_reminder",
]);

export function ObjectiveCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Audit V4-05: contexto cuando se llega desde "Usar en campaña" de un segmento.
  const incoming = location.state as
    | { segmentId?: string; segmentName?: string }
    | null;
  const { createDraft, objectives } = useObjectives(MOCK_BRAND_ID);

  const [selected, setSelected] = useState<ObjectivePreset | null>(null);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Si la marca ya tiene una automática activa con este preset, mostramos info */
  const existingAutomatic = useMemo(() => {
    if (!selected) return null;
    return (
      objectives.find(
        (o) =>
          o.preset === selected &&
          o.origin === "automatic" &&
          (o.status === "active" || o.status === "paused"),
      ) || null
    );
  }, [selected, objectives]);

  const isTypicallyAutomatic = selected
    ? TYPICALLY_AUTOMATIC_PRESETS.has(selected)
    : false;

  const handleNext = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    const draft = await createDraft({
      preset: selected,
      name: name.trim() || undefined,
    });

    if (draft) {
      // Audit V4-05: arrastramos el segmento elegido para que el wizard
      // manual lo deje pre-seleccionado.
      if (incoming?.segmentId) {
        await objectivesApi.update(draft.id, { segmentId: incoming.segmentId });
      }
      setSubmitting(false);
      navigate(`/objectives/${draft.id}/setup`);
    } else {
      setSubmitting(false);
      setError("No se pudo crear la campaña. Probá de nuevo.");
    }
  };

  const presetMeta = selected ? PRESET_BY_KEY[selected] : null;

  return (
    <div className="min-h-screen p-6 md:p-8 animate-fade-in">
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Breadcrumb */}
        <Link
          to="/objectives"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors"
        >
          <ArrowLeft size={14} />
          Volver a campañas
        </Link>

        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
              Nueva campaña
            </h1>
            <span className="px-2.5 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-semibold flex items-center gap-1">
              🎨 Va a quedar como "Mía"
            </span>
            <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-semibold">
              Paso 1: Elegir tipo
            </span>
          </div>
          <p className="text-slate-500 text-base leading-relaxed">
            Elegí un punto de partida. Después de configurar segmento, plantilla
            y cadencia, la IA revisa todo antes de activar. Las campañas{" "}
            <strong>automáticas</strong> (carrito, inactivos, bienvenida…) ya
            las maneja Deenex por vos — no hace falta crearlas.
          </p>
        </div>

        {incoming?.segmentId && (
          <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4 flex items-center gap-3 animate-fade-in">
            <Info size={16} className="text-violet-600 flex-shrink-0" />
            <p className="text-sm text-violet-900">
              Vas a usar el segmento{" "}
              <strong>{incoming.segmentName || "seleccionado"}</strong> en esta
              campaña. Elegí el tipo y seguí.
            </p>
          </div>
        )}

        {/* Preset picker */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <h2 className="font-semibold text-slate-900 mb-1">
            ¿Qué tipo de campaña querés?
          </h2>
          <p className="text-xs text-slate-500 mb-5">
            Cada preset incluye un blueprint que podés ajustar después.
          </p>

          <ObjectivePresetPicker selected={selected} onSelect={setSelected} />
        </div>

        {/* ─── Banner contextual: lo que vas a crear con el modelo de 3 tipos ───
            Va INMEDIATAMENTE después del picker (no al final de la página)
            para que el usuario lo vea sin scrollear cuando selecciona. */}
        {selected && existingAutomatic && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5 animate-fade-in flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0">
              <Zap size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-indigo-900 mb-1">
                Ya tenés "{existingAutomatic.name}" corriendo como{" "}
                automática always-on
              </h3>
              <p className="text-xs text-indigo-800 leading-relaxed mb-3">
                Deenex la activó por defecto y va corriendo en{" "}
                <strong>background</strong>. Si querés tocar el copy o el
                segmento, conviene editar la existente en vez de crear otra (te
                impactaría a los mismos usuarios duplicado).
              </p>
              <div className="flex flex-wrap gap-2">
                <Link to={`/objectives/${existingAutomatic.id}/setup`}>
                  <Button variant="secondary" className="!py-1.5 !px-3 text-xs">
                    Editar la que ya corre →
                  </Button>
                </Link>
                <button
                  onClick={() => setSelected(null)}
                  className="text-xs font-medium text-indigo-700 hover:underline px-2 py-1.5"
                >
                  Elegir otro tipo
                </button>
              </div>
            </div>
          </div>
        )}

        {selected && !existingAutomatic && (
          <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4 animate-fade-in flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center flex-shrink-0">
              <Info size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-violet-900 leading-relaxed">
                <strong>Esta queda como "Mía"</strong> — vos la configurás y la
                IA la revisa antes de activarse. Si todo OK, sale; si detecta
                algo (tono, spam risk, choque de frecuencia), te lo señala para
                que ajustes.
                {isTypicallyAutomatic && (
                  <>
                    {" "}
                    Como esta lógica usualmente la maneja Deenex automáticamente,
                    podrías terminar duplicando esfuerzo — pensalo.
                  </>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Bloque largo: agregar campaña personalizada (solo visible cuando no hay preset seleccionado) */}
        {!selected && <Link
          to="/objectives/new/custom"
          className="block relative overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-gradient-to-br from-slate-50 via-white to-violet-50/40 hover:border-violet-400 hover:from-violet-50/40 hover:via-white hover:to-fuchsia-50/40 transition-all duration-300 group"
        >
          <div className="absolute -right-12 -top-12 w-48 h-48 bg-gradient-to-br from-violet-200/40 to-fuchsia-200/30 rounded-full blur-3xl pointer-events-none group-hover:scale-110 transition-transform duration-500" />

          <div className="relative flex flex-col md:flex-row md:items-center gap-5 p-6 md:p-8">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow-lg shadow-violet-500/30 group-hover:scale-105 group-hover:rotate-3 transition-transform duration-300">
                <Wand2 size={28} />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 flex items-center gap-1">
                  <Sparkles size={9} />
                  Sin plantilla
                </span>
                <span className="text-[10px] font-semibold text-slate-500">
                  Para experimentos o campañas únicas
                </span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 tracking-tight">
                Agregar campaña personalizada
              </h3>
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed max-w-2xl">
                ¿Ninguno de los 18 presets encaja? Te abrimos una página
                dedicada donde definís nombre, qué querés lograr y qué métricas
                mejorar — la IA te asiste con audiencia, plantilla y cadencia.
              </p>
            </div>

            <div className="flex-shrink-0">
              <div className="bg-white border border-violet-200 text-violet-700 group-hover:bg-violet-600 group-hover:text-white group-hover:border-violet-600 font-semibold px-5 py-3 rounded-xl flex items-center gap-2 transition-all duration-300 shadow-sm">
                Configurar
                <ArrowRight
                  size={15}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </div>
            </div>
          </div>
        </Link>}

        {/* Custom name (opcional para presets normales) */}
        {selected && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 animate-fade-in">
            <label htmlFor="campaign-name" className="block">
              <span className="font-semibold text-slate-900">
                Nombre de la campaña
              </span>
              <span className="text-xs text-slate-500 ml-1">(opcional)</span>
            </label>
            <p className="text-xs text-slate-500 mb-3 mt-1">
              Si lo dejás vacío usamos:{" "}
              <span className="font-medium text-slate-700">
                {presetMeta?.name}
              </span>
            </p>
            <input
              id="campaign-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 80))}
              placeholder={presetMeta?.name}
              maxLength={80}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400
                focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all"
            />
            <p className="text-[10px] text-slate-400 mt-1 text-right font-mono">
              {name.length}/80
            </p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 animate-fade-in">
            <AlertCircle size={18} />
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}

        {/* CTA bar */}
        <div className="flex items-center justify-end gap-3">
          <Link to="/objectives">
            <Button variant="secondary" disabled={submitting}>
              Cancelar
            </Button>
          </Link>
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={!selected || submitting}
          >
            {submitting ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Creando…
              </>
            ) : (
              <>
                Continuar
                <ArrowRight size={15} />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ObjectiveCreatePage;
